import * as Yup from 'yup';

import { VALIDATION_CONSTANTS } from 'global/constants';

import { areExpertContactsEnabled } from 'utils/FormUtils';
import { YPhoneNumber } from 'utils/YupUtils';

export const EXPERT_CONTACT_ERRORS = {
  DUPLICATE_EMAIL: 'Die E-Mail-Adresse ist bereits vergeben',
  DUPLICATE_EMAIL_DIFFERENT:
    'Die neue E-Mail-Adresse muss von der aktuellen E-Mail-Adresse abweichen',
  CONFIRM_EMAIL_REQUIRED_CREATE: 'Bitte bestätigen Sie die E-Mail-Adresse',
  CONFIRM_EMAIL_REQUIRED_EDIT: 'Bitte bestätigen Sie die neue E-Mail-Adresse',
  EMAIL_MISMATCH: 'Die E-Mail-Adressen stimmen nicht überein',
  UPDATE_FAILED:
    'Beim Aktualisieren der Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
};

export const getExpertEmails = (experts = []) =>
  experts.map((expert) => (expert?.netzportalEmail || '').trim().toLowerCase()).filter(Boolean);

export const buildExpertContactValidationFields = ({
  forbiddenMails = [],
  normalizedCurrentEmail = '',
  isEditing = false,
  emailRequired = false,
}) => {
  const telefonField = areExpertContactsEnabled ? YPhoneNumber : Yup.string().nullable().optional();
  const normalizedForbiddenMails = forbiddenMails
    .map((mail) => String(mail ?? '').trim().toLowerCase())
    .filter(Boolean);

  const isDuplicateEmail = (value) => {
    if (!value) {
      return true;
    }
    return !normalizedForbiddenMails.includes(value.trim().toLowerCase());
  };

  if (isEditing) {
    return {
      netzportalEmail: Yup.string().email(VALIDATION_CONSTANTS.INVALID_EMAIL).optional(),
      newEmail: Yup.string()
        .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
        .optional()
        .test(
          'new-email-different-from-current',
          EXPERT_CONTACT_ERRORS.DUPLICATE_EMAIL_DIFFERENT,
          (val) => {
            if (!val) {
              return true;
            }
            return val.trim().toLowerCase() !== normalizedCurrentEmail;
          }
        )
        .test('new-email-not-duplicate', EXPERT_CONTACT_ERRORS.DUPLICATE_EMAIL, isDuplicateEmail),
      confirmEmail: Yup.string()
        .optional()
        .when('newEmail', {
          is: (val) => Boolean(val),
          then: (schema) =>
            schema
              .required(EXPERT_CONTACT_ERRORS.CONFIRM_EMAIL_REQUIRED_EDIT)
              .oneOf([Yup.ref('newEmail')], EXPERT_CONTACT_ERRORS.EMAIL_MISMATCH),
        }),
      telefon: telefonField,
    };
  }

  let netzportalEmailSchema = Yup.string()
    .email(VALIDATION_CONSTANTS.INVALID_EMAIL)
    .test('email-not-duplicate', EXPERT_CONTACT_ERRORS.DUPLICATE_EMAIL, isDuplicateEmail);

  if (emailRequired) {
    netzportalEmailSchema = netzportalEmailSchema.required(VALIDATION_CONSTANTS.MANDATORY);
  } else {
    netzportalEmailSchema = netzportalEmailSchema.optional();
  }

  const confirmEmailSchema = emailRequired
    ? Yup.string()
        .required(EXPERT_CONTACT_ERRORS.CONFIRM_EMAIL_REQUIRED_CREATE)
        .oneOf([Yup.ref('netzportalEmail')], EXPERT_CONTACT_ERRORS.EMAIL_MISMATCH)
    : Yup.string()
        .optional()
        .when('netzportalEmail', {
          is: (val) => Boolean(val),
          then: (schema) =>
            schema
              .required(EXPERT_CONTACT_ERRORS.CONFIRM_EMAIL_REQUIRED_CREATE)
              .oneOf([Yup.ref('netzportalEmail')], EXPERT_CONTACT_ERRORS.EMAIL_MISMATCH),
        });

  return {
    netzportalEmail: netzportalEmailSchema,
    confirmEmail: confirmEmailSchema,
    telefon: telefonField,
  };
};

export const getExpertContactFormErrorMessage = (errors = {}) =>
  errors.telefon || errors.newEmail || errors.confirmEmail || errors.netzportalEmail || '';

export const setCreateExpertEmailFieldErrors = (formik, message) => {
  formik.setFieldError('netzportalEmail', message);
  if (formik.values.confirmEmail) {
    formik.setFieldError('confirmEmail', message);
  }
};

export const resolveExpertContactEmail = (values, { isEditing = false } = {}) =>
  isEditing ? values.newEmail || values.netzportalEmail : values.netzportalEmail;

export const buildUpdateContactsVariables = (expertId, values, { isEditing = false } = {}) => ({
  input: {
    fachkraefte: [
      {
        id: expertId,
        telefon: values.telefon,
        netzportalEmail: resolveExpertContactEmail(values, { isEditing }),
      },
    ],
  },
});

export const isDuplicateEmailApiStatus = (status) => [409, 400].includes(status);

export const hasExpertContactValues = (values, { isEditing = false } = {}) =>
  Boolean(resolveExpertContactEmail(values, { isEditing }) || values.telefon);

export const shouldPersistExpertContacts = (expert, { isEditing = false } = {}) => {
  if (!areExpertContactsEnabled || !expert?.id) {
    return false;
  }

  return hasExpertContactValues(expert, { isEditing });
};

export const submitExpertContactsUpdate = (
  updateContactsMutation,
  expertId,
  values,
  options = {}
) => {
  const { isEditing = false } = options;
  const resolvedEmail = resolveExpertContactEmail(values, { isEditing });

  return updateContactsMutation({
    variables: buildUpdateContactsVariables(expertId, values, { isEditing }),
  }).then(({ data }) => {
    const status = data?.updateInstallerExpertTelephoneEmail?.status;

    if (isDuplicateEmailApiStatus(status)) {
      return { success: false, duplicateEmail: true };
    }

    if (resolvedEmail && (isEditing ? values.newEmail : true)) {
      return { success: true, needsEmailConfirmation: true };
    }

    return {
      success: true,
      needsEmailConfirmation: false,
      telefon: values.telefon,
      netzportalEmail: resolvedEmail || values.netzportalEmail,
    };
  });
};
